/**
 * "اختر التخصص" — the specialty grid.
 *
 * One tile per specialty, each in its own colour, its icon well lifting and
 * lighting up on hover. The legacy grid painted all twenty-two tiles the same
 * cyan (`renderCategories`, app.js:1572), which made them a wall of identical
 * squares; the colour is what lets you find a specialty by shape rather than by
 * reading every label.
 *
 * The glow is driven by two CSS custom properties the `.category-card` rule in
 * index.css reads, so the hover effect is one stylesheet rule rather than a
 * per-tile inline transition.
 */

import { Link } from 'react-router-dom';
import { Icon } from '../../components/common';
import { categoryAccent, categoryIcon } from '../../domain/catalog';

export function CategoryGrid({ categories = [], activeId = null, className = '' }) {
  return (
    <div
      className={`grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${className}`}
    >
      {categories.map((category) => (
        <CategoryTile
          key={category.id}
          category={category}
          active={String(activeId) === String(category.id)}
        />
      ))}
    </div>
  );
}

function CategoryTile({ category, active }) {
  const accent = categoryAccent(category.id);
  const count = category.count || 0;

  return (
    <Link
      to={`/patient/doctors/${category.id}`}
      aria-current={active ? 'page' : undefined}
      className="category-card group flex flex-col items-center gap-2 rounded-2xl border p-4 text-center"
      style={{
        // Read by the .category-card hover rule so each tile glows its own hue.
        '--glow': accent.glow,
        '--glow-soft': accent.glowSoft,
        borderColor: active ? accent.glow : 'rgba(255,255,255,0.05)',
        background: active ? accent.tint : 'rgba(255,255,255,0.04)',
      }}
    >
      <span
        className="category-icon flex h-12 w-12 items-center justify-center rounded-xl"
        style={{ background: accent.tint, color: accent.color }}
      >
        <Icon name={category.icon || categoryIcon(category.id)} size={24} />
      </span>

      <span className="text-xs font-bold leading-tight text-white">{category.name}</span>

      {/* "لا يوجد" rather than "0 طبيب": an empty specialty is a state, not a
          count, and reads better than a zero. */}
      <span className="text-[10px] text-slate-500">{count > 0 ? `${count} طبيب` : 'لا يوجد'}</span>
    </Link>
  );
}

export default CategoryGrid;
